import { useState, useEffect } from 'react';
import { cryptoAPI } from '../services/api';
import { mockCryptos } from '../mockData';
import { COMMODITIES } from '../utils/commodities';

export const useCryptoData = (limit = 50) => {
  const [cryptos, setCryptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchCryptos = async () => {
      try {
        setLoading(true);
        const data = await cryptoAPI.getList(limit);
        
        if (!isMounted) return;
        
        // Transform CoinGecko data to match our format
        let transformedData = data.map(crypto => ({
          id: crypto.id,
          symbol: crypto.symbol,
          name: crypto.name,
          current_price: crypto.current_price,
          price_change_percentage_24h: crypto.price_change_percentage_24h || 0,
          market_cap: crypto.market_cap,
          total_volume: crypto.total_volume,
          image: crypto.image
        }));

        // Prepend Gold & Silver; their tracking-token ids keep the binary price flow unchanged.
        const metals = await Promise.all(COMMODITIES.map(async (c) => {
          try {
            const res = await cryptoAPI.getById(c.id);
            if (!res) return null;
            const md = res.market_data || {};
            return {
              id: c.id,
              symbol: c.symbol,
              name: c.name,
              current_price: res.current_price || md.current_price?.usd || 0,
              price_change_percentage_24h: res.price_change_percentage_24h || md.price_change_percentage_24h || 0,
              market_cap: res.market_cap || md.market_cap?.usd || 0,
              total_volume: res.total_volume || md.total_volume?.usd || 0,
              image: c.icon,
              is_commodity: true,
              quote_currency: 'USD',
            };
          } catch (_e) {
            return null;
          }
        }));
        const metalIds = COMMODITIES.map((c) => c.id);
        transformedData = [
          ...metals.filter(Boolean),
          ...transformedData.filter((c) => !metalIds.includes(c.id)),
        ];

        console.log('✅ Loaded real-time crypto data:', transformedData.slice(0, 3).map(c => `${c.symbol}: $${c.current_price}`));
        setCryptos(transformedData);
        setError(null);
      } catch (err) {
        console.error('❌ Failed to fetch crypto data:', err);
        if (isMounted) {
          setError(err.message);
          // Only use mock data as fallback if API completely fails
          console.warn('⚠️ Using mock data as fallback');
          setCryptos(mockCryptos);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCryptos();

    // Refresh every 30 seconds
    const interval = setInterval(fetchCryptos, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [limit]);

  return { cryptos, loading, error };
};
